import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { formatDate } from '../../utils/helper';
import { getEventColor } from './eventColors';
import './InspectionHistoryTimeline.css';

const VIEWPORT_PADDING = 12;
const TOOLTIP_GAP = 10;

const HistoryTooltip = ({ event, anchorRect }) => {
    const tooltipRef = useRef(null);
    const [position, setPosition] = useState({ top: 0, left: 0, placement: 'top' });

    useLayoutEffect(() => {
        if (!anchorRect || !tooltipRef.current) return;

        const tooltipEl = tooltipRef.current;
        const { width, height } = tooltipEl.getBoundingClientRect();
        const anchorCenterX = anchorRect.left + anchorRect.width / 2;

        let left = anchorCenterX - width / 2;
        left = Math.max(VIEWPORT_PADDING, Math.min(left, window.innerWidth - width - VIEWPORT_PADDING));

        const spaceAbove = anchorRect.top - VIEWPORT_PADDING;
        const spaceBelow = window.innerHeight - anchorRect.bottom - VIEWPORT_PADDING;
        let top;
        let placement = 'top';

        if (spaceAbove >= height + TOOLTIP_GAP || spaceAbove >= spaceBelow) {
            top = anchorRect.top - height - TOOLTIP_GAP;
            placement = 'top';
        } else {
            top = anchorRect.bottom + TOOLTIP_GAP;
            placement = 'bottom';
        }

        top = Math.max(VIEWPORT_PADDING, Math.min(top, window.innerHeight - height - VIEWPORT_PADDING));

        setPosition({ top, left, placement });
    }, [anchorRect, event]);

    return createPortal(
        <div
            ref={tooltipRef}
            className={`inspection-history-tooltip inspection-history-tooltip--floating inspection-history-tooltip--${position.placement}`}
            style={{ top: position.top, left: position.left }}
            role="tooltip"
        >
            <p className="inspection-history-tooltip-title">{event.label}</p>
            <p className="inspection-history-tooltip-date">
                {formatDate(event.timestamp)}
            </p>
            {event.actor?.name && (
                <p className="inspection-history-tooltip-actor">
                    Por: {event.actor.name}
                    {event.actor.role ? ` (${event.actor.role})` : ''}
                </p>
            )}
            {event.details && (
                <p className="inspection-history-tooltip-details">
                    {event.details}
                </p>
            )}
            {event.metadata?.inferred && (
                <span className="inspection-history-inferred-badge">
                    Evento reconstruido
                </span>
            )}
        </div>,
        document.body
    );
};

const InspectionHistoryTimeline = ({ statusHistory = [] }) => {
    const [tooltipState, setTooltipState] = useState(null);
    const scrollRef = useRef(null);

    const sortedEvents = useMemo(() => {
        return [...statusHistory].sort(
            (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
        );
    }, [statusHistory]);

    const updateTooltipPosition = useCallback(() => {
        setTooltipState((current) => {
            if (!current?.anchorEl) return current;
            return {
                ...current,
                anchorRect: current.anchorEl.getBoundingClientRect(),
            };
        });
    }, []);

    const showTooltip = useCallback((event, anchorEl) => {
        setTooltipState({
            event,
            anchorEl,
            anchorRect: anchorEl.getBoundingClientRect(),
        });
    }, []);

    const hideTooltip = useCallback(() => {
        setTooltipState(null);
    }, []);

    useEffect(() => {
        if (!tooltipState) return undefined;

        const scrollContainer = scrollRef.current;
        window.addEventListener('scroll', updateTooltipPosition, true);
        window.addEventListener('resize', updateTooltipPosition);
        scrollContainer?.addEventListener('scroll', updateTooltipPosition);

        return () => {
            window.removeEventListener('scroll', updateTooltipPosition, true);
            window.removeEventListener('resize', updateTooltipPosition);
            scrollContainer?.removeEventListener('scroll', updateTooltipPosition);
        };
    }, [tooltipState, updateTooltipPosition]);

    if (!sortedEvents.length) {
        return (
            <p className="inspection-history-empty">
                No hay eventos registrados para esta inspección.
            </p>
        );
    }

    return (
        <div className="inspection-history-timeline">
            {sortedEvents.length === 1 && (
                <p className="inspection-history-hint">
                    El historial se irá completando conforme avance la inspección.
                </p>
            )}

            <div className="inspection-history-scroll" ref={scrollRef}>
                <div className="inspection-history-track">
                    {sortedEvents.map((event, index) => {
                        const isLast = index === sortedEvents.length - 1;
                        const isActive = tooltipState?.event?.id === event.id;
                        const color = getEventColor(event);

                        return (
                            <div
                                key={event.id}
                                className={`inspection-history-node-wrapper${isActive ? ' is-active' : ''}`}
                                onMouseEnter={(e) => showTooltip(event, e.currentTarget)}
                                onMouseLeave={hideTooltip}
                                onFocus={(e) => showTooltip(event, e.currentTarget)}
                                onBlur={hideTooltip}
                                tabIndex={0}
                            >
                                <div className="inspection-history-node-row">
                                    <div
                                        className="inspection-history-dot"
                                        style={{ backgroundColor: color, borderColor: color }}
                                        aria-hidden="true"
                                    />
                                    {!isLast && <div className="inspection-history-connector" />}
                                </div>

                                <span className="inspection-history-label" title={event.label}>
                                    {event.label}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>

            {tooltipState?.event && tooltipState?.anchorRect && (
                <HistoryTooltip
                    event={tooltipState.event}
                    anchorRect={tooltipState.anchorRect}
                />
            )}
        </div>
    );
};

export default InspectionHistoryTimeline;
